import { Dayjs } from "dayjs";
import { dayjs } from "@/utils/dayjs";
import { EMPTY_STR } from "@/constants/string";

export const FILTER_LABEL_MAX_LENGTH = 30;
export const FILTER_LABEL_FULL_WIDTH_MAX_LENGTH = 60;

export type TCaseListFilterFormExtended = {
  caseStatus: string[];
  managementNumber: string;
  caseName: string;
  jobName: string;
  companyName: string[];
  companySpecificIndustryId: string[];
  workplaceProvinceCode: string[];
  workplaceMunicipalityCode: string[];
  recruitmentNumber: string[];
  caseMatchCount: string[];
  personInChargeId: string[];
  registrationDateFrom: Dayjs | null;
  registrationDateTo: Dayjs | null;
};

export type TCaseListDisplayColumnSetting = {
  id: string;
  label: string;
  visible: boolean;
  order: number;
};

export const defaultCaseListFilterValues: TCaseListFilterFormExtended = {
  caseStatus: [],
  managementNumber: "",
  caseName: "",
  jobName: "",
  companyName: [],
  companySpecificIndustryId: [],
  workplaceProvinceCode: [],
  workplaceMunicipalityCode: [],
  recruitmentNumber: [],
  caseMatchCount: [],
  personInChargeId: [],
  registrationDateFrom: null,
  registrationDateTo: null,
};

export const defaultCaseListDisplayColumns: TCaseListDisplayColumnSetting[] = [
  { id: "progressStatus", label: "ステータス", visible: true, order: 1 },
  { id: "managementNumber", label: "案件管理番号", visible: true, order: 2 },
  { id: "name", label: "案件名", visible: true, order: 3 },
  { id: "jobName", label: "求人名", visible: true, order: 4 },
  { id: "companyName", label: "企業名", visible: true, order: 5 },
  { id: "companySpecificIndustry", label: "分野", visible: true, order: 6 },
  { id: "workplace", label: "勤務地", visible: true, order: 7 },
  { id: "recruitmentNumber", label: "募集人数", visible: true, order: 8 },
  { id: "caseMatchCount", label: "提案人数", visible: true, order: 9 },
  { id: "fullName", label: "案件担当者", visible: true, order: 10 },
  { id: "createdAt", label: "登録日", visible: true, order: 11 },
];

export const LOCKED_CASE_LIST_COLUMN_IDS = ["progressStatus", "name"];

export const CASE_LIST_FILTER_FIELD_LABELS: { key: keyof TCaseListFilterFormExtended; label: string }[] = [
  { key: "caseStatus", label: "ステータス" },
  { key: "managementNumber", label: "案件管理番号" },
  { key: "caseName", label: "案件名" },
  { key: "jobName", label: "求人名" },
  { key: "companyName", label: "企業名" },
  { key: "companySpecificIndustryId", label: "分野" },
  { key: "workplaceProvinceCode", label: "勤務地（都道府県）" },
  { key: "workplaceMunicipalityCode", label: "勤務地（市区町村）" },
  { key: "recruitmentNumber", label: "募集人数" },
  { key: "caseMatchCount", label: "提案人数" },
  { key: "personInChargeId", label: "案件担当者" },
];

/** Multi-select filter field keys */
export const MULTI_SELECT_FILTER_KEYS: (keyof TCaseListFilterFormExtended)[] = [
  "caseStatus",
  "companyName",
  "companySpecificIndustryId",
  "workplaceProvinceCode",
  "workplaceMunicipalityCode",
  "recruitmentNumber",
  "caseMatchCount",
  "personInChargeId",
];

/** Check if a filter value is empty (empty string, null, undefined, or empty array) */
export const isEmptyFilterValue = (value: unknown): boolean => {
  if (value === "" || value === null || value === undefined) return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
};

/** Normalize legacy scalar filter values to arrays for backward compatibility with saved conditions */
export const normalizeMultiSelectFields = (filters: Partial<Record<string, unknown>>): Partial<TCaseListFilterFormExtended> => {
  const result = { ...filters };
  for (const key of MULTI_SELECT_FILTER_KEYS) {
    const value = result[key];
    if (value === undefined) continue;
    if (Array.isArray(value)) continue;
    if (value === "" || value === null) {
      result[key] = [];
    } else {
      result[key] = [String(value)];
    }
  }
  return result as Partial<TCaseListFilterFormExtended>;
};

export const stripEmptyCaseListFilterFields = (filters: TCaseListFilterFormExtended): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (isEmptyFilterValue(value)) continue;
    result[key] = value;
  }
  return result;
};

export const CASE_DATE_RANGE_FIELD = {
  fromKey: "registrationDateFrom" as const,
  toKey: "registrationDateTo" as const,
  label: "登録日",
};

export const normalizeCaseDateFields = (filters: Partial<TCaseListFilterFormExtended>): Partial<TCaseListFilterFormExtended> => {
  const result = { ...filters };
  if (result.registrationDateFrom && typeof result.registrationDateFrom === "string") {
    const day = dayjs(result.registrationDateFrom);
    result.registrationDateFrom = day.isValid() ? day : null;
  }
  if (result.registrationDateTo && typeof result.registrationDateTo === "string") {
    const day = dayjs(result.registrationDateTo);
    result.registrationDateTo = day.isValid() ? day : null;
  }
  return result;
};

export const formatCaseDateValue = (value: Dayjs | string | null): string => {
  if (!value) return EMPTY_STR.DATE;
  const day = dayjs(value);
  return day.isValid() ? day.format("YYYY/MM/DD") : EMPTY_STR.DATE;
};

export const serializeCaseListForComparison = (title: string, filters: TCaseListFilterFormExtended): string => {
  const normalized = { ...filters };
  if (normalized.registrationDateFrom) {
    normalized.registrationDateFrom = dayjs(normalized.registrationDateFrom).format(
      "YYYY-MM-DD",
    ) as unknown as typeof normalized.registrationDateFrom;
  }
  if (normalized.registrationDateTo) {
    normalized.registrationDateTo = dayjs(normalized.registrationDateTo).format("YYYY-MM-DD") as unknown as typeof normalized.registrationDateTo;
  }
  return JSON.stringify({ title, filters: normalized });
};
